import { useMemo, useState } from 'react'
import { bucketMinutes } from './PlayerTrend'

/**
 * The attendance curve, big enough to read a time off.
 *
 * The row's sparkline answers "is this climbing or falling". This answers the
 * next question, which is the one worth acting on: *when*. A drop at 9:15 that
 * lines up with a server restart is a different problem from a slow bleed over
 * two hours, and a line with no axis cannot tell those apart.
 *
 * Hovering reads out the exact bucket rather than relying on the axis labels,
 * because the labels have to stay sparse to be legible and the interesting
 * point is rarely on one.
 */
const pad = (n: number) => String(n).padStart(2, '0')

const clock = (d: Date) => {
    const h = d.getHours()
    const suffix = h >= 12 ? 'PM' : 'AM'
    const hour = h % 12 === 0 ? 12 : h % 12
    return `${hour}:${pad(d.getMinutes())} ${suffix}`
}

export const PlayerTrendDetail = ({
    points,
    startsAt,
    endsAt,
    live,
    bucket,
}: {
    points: number[]
    startsAt: string
    endsAt: string
    live?: boolean
    /** The bucket the caller actually used, when it knows. The server sheet's
     *  chart is built from fixed 10-minute buckets, and guessing from the span
     *  labelled it "one point per 15 min" (SRV-38). */
    bucket?: number
}) => {
    const [hover, setHover] = useState<number | null>(null)

    const begin = new Date(startsAt).getTime()
    const finish = new Date(endsAt).getTime()
    const minutes = Math.max(1, (finish - begin) / 60000)
    const size = bucket ?? bucketMinutes(minutes)

    /** The clock time a bucket covers. Buckets are evenly spaced across the
     *  window, so the index alone gives the instant. */
    const timeAt = (i: number) =>
        new Date(begin + (i / Math.max(1, points.length - 1)) * (finish - begin))

    const peak = Math.max(...points, 1)
    const { biggestDrop, biggestRise } = useMemo(() => {
        let drop = { at: -1, by: 0 }
        let rise = { at: -1, by: 0 }
        for (let i = 1; i < points.length; i += 1) {
            const delta = points[i] - points[i - 1]
            if (delta < drop.by) drop = { at: i, by: delta }
            if (delta > rise.by) rise = { at: i, by: delta }
        }
        return { biggestDrop: drop, biggestRise: rise }
    }, [points])

    if (points.length < 2) {
        return <div className='text-white/30'>No attendance recorded for this race.</div>
    }

    const W = 560
    const H = 120
    const PADL = 28
    const PADB = 16

    const x = (i: number) => PADL + (i / (points.length - 1)) * (W - PADL - 4)
    const y = (v: number) => H - PADB - (v / peak) * (H - PADB - 8)

    const line = points.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
    const area = `${PADL},${H - PADB} ${line} ${W - 4},${H - PADB}`
    const stroke = live ? '#4ade80' : 'rgba(255,255,255,0.55)'

    // Four labels at most. More than that and they collide at this width; the
    // hover readout covers everything between them.
    const ticks = [0, Math.floor((points.length - 1) / 3), Math.floor(((points.length - 1) * 2) / 3), points.length - 1]

    return (
        <div className='flex flex-col gap-2'>
            <div className='flex flex-row items-baseline justify-between gap-3'>
                <span className='text-[10px] uppercase tracking-widest text-white/40'>
                    Players &middot; one point per {size < 60 ? `${size} min` : `${size / 60} hr`}
                </span>
                <span className='text-[10px] tabular-nums text-white/40'>
                    {/* The real peak. `peak` is floored at 1 so the scale never
                        divides by zero, which made an empty chart read "peak 1". */}
                    peak {Math.max(...points, 0)} &middot; {clock(new Date(begin))} → {clock(new Date(finish))}
                </span>
            </div>

            <svg
                viewBox={`0 0 ${W} ${H}`}
                className='w-full'
                onMouseLeave={() => setHover(null)}
                role='img'
                aria-label='Players over the course of the race'
            >
                {/* Three gridlines, labelled. Enough to judge a height against
                    without turning the panel into graph paper. */}
                {[0, 0.5, 1].map((f) => (
                    <g key={f}>
                        <line
                            x1={PADL}
                            x2={W - 4}
                            y1={y(peak * f)}
                            y2={y(peak * f)}
                            stroke='rgba(255,255,255,0.08)'
                            strokeWidth={1}
                        />
                        <text
                            x={0}
                            y={y(peak * f) + 3}
                            className='fill-white/30'
                            style={{ fontSize: 8 }}
                        >
                            {Math.round(peak * f)}
                        </text>
                    </g>
                ))}

                <polygon points={area} fill={live ? 'rgba(74,222,128,0.10)' : 'rgba(255,255,255,0.05)'} />
                <polyline
                    points={line}
                    fill='none'
                    stroke={stroke}
                    strokeWidth={1.5}
                    strokeLinejoin='round'
                    vectorEffect='non-scaling-stroke'
                />

                {/* The steepest fall and the steepest climb, marked. These are
                    the two moments somebody opens this panel to find. */}
                {biggestDrop.at > 0 && (
                    <line
                        x1={x(biggestDrop.at)}
                        x2={x(biggestDrop.at)}
                        y1={8}
                        y2={H - PADB}
                        stroke='rgba(248,113,113,0.5)'
                        strokeDasharray='2 2'
                        strokeWidth={1}
                    />
                )}
                {biggestRise.at > 0 && (
                    <line
                        x1={x(biggestRise.at)}
                        x2={x(biggestRise.at)}
                        y1={8}
                        y2={H - PADB}
                        stroke='rgba(74,222,128,0.4)'
                        strokeDasharray='2 2'
                        strokeWidth={1}
                    />
                )}

                {ticks.map((i) => (
                    <text
                        key={i}
                        x={x(i)}
                        y={H - 4}
                        textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
                        className='fill-white/35'
                        style={{ fontSize: 8 }}
                    >
                        {clock(timeAt(i))}
                    </text>
                ))}

                {/* One hit area per bucket, so the readout is exact rather than
                    guessed from the pointer's x position. */}
                {points.map((_, i) => (
                    <rect
                        key={i}
                        x={x(i) - (W - PADL) / points.length / 2}
                        y={0}
                        width={(W - PADL) / points.length}
                        height={H - PADB}
                        fill='transparent'
                        onMouseEnter={() => setHover(i)}
                    />
                ))}

                {hover !== null && (
                    <>
                        <line
                            x1={x(hover)}
                            x2={x(hover)}
                            y1={8}
                            y2={H - PADB}
                            stroke='rgba(255,255,255,0.35)'
                            strokeWidth={1}
                        />
                        <circle cx={x(hover)} cy={y(points[hover])} r={2.5} fill={stroke} />
                    </>
                )}
            </svg>

            <div className='flex flex-row flex-wrap items-center gap-x-4 gap-y-1 text-[10px] tabular-nums'>
                {hover !== null ? (
                    <span className='text-white'>
                        {clock(timeAt(hover))} &mdash;{' '}
                        <span className={live ? 'text-green-400' : ''}>{points[hover]} players</span>
                        {hover > 0 && (
                            <span
                                className={
                                    points[hover] - points[hover - 1] < 0
                                        ? 'ml-2 text-red-400'
                                        : 'ml-2 text-green-400'
                                }
                            >
                                {points[hover] - points[hover - 1] >= 0 ? '+' : ''}
                                {points[hover] - points[hover - 1]}
                            </span>
                        )}
                    </span>
                ) : (
                    <span className='text-white/30'>Hover the line for an exact time.</span>
                )}

                {biggestDrop.at > 0 && (
                    <span className='text-red-400/80'>
                        steepest fall {clock(timeAt(biggestDrop.at))} ({biggestDrop.by})
                    </span>
                )}
                {biggestRise.at > 0 && (
                    <span className='text-green-400/80'>
                        steepest climb {clock(timeAt(biggestRise.at))} (+{biggestRise.by})
                    </span>
                )}
            </div>
        </div>
    )
}
